import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { Client, ClientType } from '@/types/client'
import type { ClientWithRelations } from '@/types/database-real'

export interface UseClientsParams {
  search?: string
  type?: ClientType | null
  status?: 'active' | 'inactive' | null
  adminId?: string
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  includeRelations?: boolean // Nouvelle option pour charger les relations
}

export interface UseClientsResult {
  clients: ClientWithRelations[]
  total: number
}

export function useClients(params: UseClientsParams = {}) {
  const { user } = useAuth()
  const {
    search,
    type,
    page = 1,
    limit = 20,
    sortBy = 'created_at',
    sortOrder = 'desc',
    includeRelations = true
  } = params

  return useQuery({
    queryKey: ['clients', params],
    queryFn: async (): Promise<UseClientsResult> => {
      let query = supabase
        .from('users')
        .select('*', { count: 'exact' })
        .eq('role', 'client')

      // Search filter
      if (search && search.trim()) {
        const searchTerm = search.trim()
        query = query.or(
          `full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,company.ilike.%${searchTerm}%`
        )
      }

      // Type filter
      if (type) {
        query = query.eq('type_client', type)
      }

      // Sorting
      query = query.order(sortBy, { ascending: sortOrder === 'asc' })

      // Pagination
      const from = (page - 1) * limit
      const to = from + limit - 1
      query = query.range(from, to)

      const { data, error, count } = await query

      if (error) throw error

      if (!includeRelations) {
        return {
          clients: (data || []).map(client => ({
            ...client,
            projects_count: 0,
            factures_count: 0,
            factures_total: 0,
            factures_unpaid: 0,
            devis_count: 0,
          })),
          total: count || 0,
        }
      }

      // Récupérer les relations pour chaque client
      const clientsWithRelations = await Promise.all(
        (data || []).map(async (client) => {
          // Count des projets (ATTENTION: projects utilise user_id)
          const { count: projectsCount } = await supabase
            .from('projects')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', client.id)

          // Count et sommes des factures (ATTENTION: facture utilise utilisateur_id)
          const { data: facturesData } = await supabase
            .from('facture')
            .select('total_ttc, statut')
            .eq('utilisateur_id', client.id)

          const facturesCount = facturesData?.length || 0
          const facturesTotalTTC = facturesData?.reduce(
            (sum, f) => sum + (f.total_ttc || 0),
            0
          ) || 0
          const facturesUnpaid = facturesData
            ?.filter(f => f.statut === 'sent' || f.statut === 'draft' || f.statut === 'overdue')
            .reduce((sum, f) => sum + (f.total_ttc || 0), 0) || 0

          // Count des devis (devis utilise client_id)
          const { count: devisCount } = await supabase
            .from('devis')
            .select('*', { count: 'exact', head: true })
            .eq('client_id', client.id)

          return {
            ...client,
            projects_count: projectsCount || 0,
            factures_count: facturesCount,
            factures_total: facturesTotalTTC,
            factures_unpaid: facturesUnpaid,
            devis_count: devisCount || 0,
          } as ClientWithRelations
        })
      )

      return {
        clients: clientsWithRelations,
        total: count || 0,
      }
    },
    enabled: !!user,
  })
}

export function useClientDocuments(clientId?: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['client', clientId, 'documents'],
    queryFn: async () => {
      if (!clientId) return []

      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('user_id', clientId)
        .order('created_at', { ascending: false })

      if (error) {
        console.warn('Error fetching client documents:', error)
        return []
      }

      // Generate signed URLs for documents (private buckets)
      return await Promise.all(
        (data || []).map(async (doc: Record<string, unknown>) => {
          const bucket = (doc.storage_bucket as string) || 'documents'
          const filePath = doc.file_path as string
          let url: string | null = null
          if (filePath) {
            const { data: urlData, error: urlError } = await supabase.storage
              .from(bucket)
              .createSignedUrl(filePath, 3600)
            if (urlError) {
              console.warn('Error signing document URL:', urlError)
            }
            url = urlData?.signedUrl ?? null
          }

          return {
            id: doc.id,
            name: doc.name,
            file_type: doc.file_type,
            file_size: doc.file_size,
            url,
            category: doc.category,
            description: doc.description,
            created_at: doc.created_at,
            uploader: doc.uploader ? {
              id: (doc.uploader as Record<string, unknown>).id,
              full_name: `${(doc.uploader as Record<string, unknown>).prenom || ''} ${(doc.uploader as Record<string, unknown>).nom || ''}`.trim(),
              email: (doc.uploader as Record<string, unknown>).email,
            } : null,
          }
        })
      )
    },
    enabled: !!user && !!clientId,
  })
}

export function useDeleteClientDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ documentId, clientId }: { documentId: string; clientId: string }) => {
      // Get document URL to extract storage path
      const { data: doc } = await supabase
        .from('documents')
        .select('url')
        .eq('id', documentId)
        .single()

      if (doc?.url) {
        // Extract file path from storage URL and delete from storage
        const match = doc.url.match(/\/storage\/v1\/object\/public\/([^/]+)\/(.+)/)
        if (match) {
          const bucket = match[1]
          const filePath = match[2]
          await supabase.storage.from(bucket).remove([filePath])
        }
      }

      const { error } = await supabase.from('documents').delete().eq('id', documentId)
      if (error) throw error
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['client', variables.clientId, 'documents'] })
    },
  })
}
